// The page's head, crumbs, cards, notes and buttons, as every admin board draws them.
import { useEffect } from 'react'
import { Link } from '../../lib/router.jsx'
import { setTitle } from '../../config/site.js'
import { Icon } from '../shell/Icons.jsx'

// The eyebrow and heading at the top of every admin page, with the page's actions (or a line) on the
// right, and a line under the heading where the board draws one. It sets the tab's title too, as the
// boards name them: "Admin: <title> | Adam Slee Members".
//   <PageHeader eyebrow="Everyone with a login" title="People" actions={<Button ...>} />
//   <PageHeader eyebrow="Wednesday 30 September" title="Today" aside="3 things need you" />
//   <PageHeader eyebrow="Stripe sells, the hub opens" title="Price map" lede="Every Stripe price, and what it opens when someone pays." />
export function PageHeader({ eyebrow, title, lede, aside, actions, docTitle, id }) {
  useEffect(() => { setTitle(docTitle || (title ? `Admin: ${title}` : 'Admin')) }, [docTitle, title])
  return (
    <div className="ad-head">
      <div className="ad-head-text">
        {eyebrow && <span className="ad-eyebrow">{eyebrow}</span>}
        <h1 className="ad-h1" id={id}>{title}</h1>
        {lede && <p className="ad-head-lede">{lede}</p>}
      </div>
      {aside && <span className="ad-head-aside">{aside}</span>}
      {actions && <div className="ad-head-actions">{actions}</div>}
    </div>
  )
}

// People / Sam Carter: items [{ label, to }], the last 1 is where they are
export function Crumbs({ items }) {
  return (
    <nav className="ad-crumbs" aria-label="Where you are">
      {items.map((item, i) => (
        <span key={item.label + i}>
          {i > 0 && ' / '}
          {item.to && i < items.length - 1 ? <Link to={item.to}>{item.label}</Link> : <span aria-current="page">{item.label}</span>}
        </span>
      ))}
    </nav>
  )
}

// A bordered section with a heading row: <Card title="Access" aside="What Sam can open, and why">
// body: false when the children are rows of their own (a Table, a list with its own padding)
export function Card({ title, aside, actions, children, body = true, headingLevel = 2, className = '', id, labelledBy }) {
  const H = `h${headingLevel}`
  const headId = id ? `${id}-h` : undefined
  return (
    <section className={`ad-card ${className}`.trim()} id={id} aria-labelledby={labelledBy || (title ? headId : undefined)}>
      {(title || aside || actions) && (
        <div className="ad-card-head">
          {title && <H className="ad-card-title" id={headId}>{title}</H>}
          {aside && <span className="ad-card-aside">{aside}</span>}
          {actions}
        </div>
      )}
      {body ? <div className="ad-card-body">{children}</div> : children}
    </section>
  )
}

// The grey note with an icon: "Nothing is uploaded here. Every recording stays on Vimeo ..."
export function InfoNote({ icon = 'info', children }) {
  return (
    <div className="ad-note" role="note">
      <Icon name={icon} />
      <span>{children}</span>
    </div>
  )
}

// Buttons: kind 'ink' (the black 1), 'line' (white with a grey edge) or 'quiet'; size 'md', 'sm', 'xs'.
// busy disables it and, with busyLabel, says what's happening.
export function Button({ kind = 'line', size = 'md', busy = false, busyLabel, children, className = '', type = 'button', disabled, ...rest }) {
  const cls = `ad-btn ad-btn--${kind}${size === 'md' ? '' : ` ad-btn--${size}`} ${className}`.trim()
  return (
    <button type={type} className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy && busyLabel ? busyLabel : children}
    </button>
  )
}

// A link that looks like a button: <ButtonLink to="/admin/products/new" kind="ink">Add a product</ButtonLink>
export function ButtonLink({ to, kind = 'line', size = 'md', children, className = '', ...rest }) {
  const cls = `ad-btn ad-btn--${kind}${size === 'md' ? '' : ` ad-btn--${size}`} ${className}`.trim()
  const external = /^[a-z][a-z0-9+.-]*:/i.test(to)
  return external
    ? <a href={to} className={cls} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
    : <Link to={to} className={cls} {...rest}>{children}</Link>
}
